/**
 * Structural theme folder contract: both markers must exist under `themes/{slug}/`.
 * Vite globs must stay literal - call sites that tree-shake use inline
 * `import.meta.env.VITE_ACTIVE_THEME && import.meta.env.VITE_ACTIVE_THEME !== 'default'`.
 */
const themeCssMarkers = import.meta.glob('./*/theme.css')
const themeConfigMarkers = import.meta.glob('./*/config.ts')

export function readStructuralThemeBuildSlug(
  rawSlug: unknown = import.meta.env.VITE_ACTIVE_THEME,
): string | undefined {
  if (typeof rawSlug !== 'string') {
    return undefined
  }

  const slug = rawSlug.trim()
  if (slug === '' || slug === 'default') {
    return undefined
  }

  return slug
}

export function hasStructuralThemeFolder(slug: string): boolean {
  const cssKey = `./${slug}/theme.css`
  const configKey = `./${slug}/config.ts`

  return cssKey in themeCssMarkers && configKey in themeConfigMarkers
}

export function assertStructuralThemeFolder(slug: string): void {
  if (!hasStructuralThemeFolder(slug)) {
    throw new Error(
      `VITE_ACTIVE_THEME [${slug}] requires themes/${slug}/theme.css and themes/${slug}/config.ts.`,
    )
  }
}

function slugFromMarkerKey(key: string, fileName: 'theme.css' | 'config.ts'): string | undefined {
  const match = key.replace(/\\/g, '/').match(new RegExp(`^\\.\\/([^/]+)\\/${fileName.replace('.', '\\.')}$`))
  return match?.[1]
}

export function listStructuralThemeFolderSlugs(): string[] {
  const cssSlugs = new Set(
    Object.keys(themeCssMarkers)
      .map((key) => slugFromMarkerKey(key, 'theme.css'))
      .filter((slug): slug is string => slug !== undefined),
  )
  const configSlugs = new Set(
    Object.keys(themeConfigMarkers)
      .map((key) => slugFromMarkerKey(key, 'config.ts'))
      .filter((slug): slug is string => slug !== undefined),
  )

  return [...cssSlugs].filter((slug) => configSlugs.has(slug)).sort()
}
